import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, CircularProgress, Collapse, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { DEFAULT_EVSE_FALLBACK_MAX_CURRENT } from 'src/Libs/flow/constants'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { getModbusTcpPUTDto } from 'src/Libs/flow/parsers'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { TabButton, TabButtonGroupBase } from 'src/Components/TabButton'
import { SettingInfoBox, SettingsBox } from 'src/Elements/Data'
import { OfflineBehaviourFallbackField } from './OfflineBehaviourFallbackField.js'
import { ControlledModbusTcpPUTDto, updateEvse } from '../../api/evse.api.js'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import useAppRatingContext from 'src/Libs/app-rating/hooks/use-app-rating-context.js'
import { usePortalMutation } from 'src/Libs/api'

const OFFLINE_BEHAVIOUR_OPTIONS = [
  {
    value: false,
    labelKey: 'user-settings.evse-offline-page.options.stop',
    descriptionKey: 'user-settings.evse-offline-page.options.stop-description',
  },
  {
    value: true,
    labelKey: 'user-settings.evse-offline-page.options.continue',
    descriptionKey: 'user-settings.evse-offline-page.options.continue-description',
  },
]

interface OfflineBehaviourProps {
  installationId: number
  queryIsLoading: boolean
  evse: ModbusTcpChargingStation
}

/** The offline behaviour page of an installation's (user-level) charging station settings. */
export const OfflineBehaviour: React.FC<OfflineBehaviourProps> = ({ installationId, queryIsLoading, evse }) => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const { triggerAppRating } = useAppRatingContext()

  const currentChargeFallbackAllowed = (evse.fallbackMaxCurrent || 0) > 0
  const [chargeFallbackAllowed, setChargeFallbackAllowed] = useLatestState(currentChargeFallbackAllowed)

  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()
  const mutateEvse = usePortalMutation({
    mutationFn: (newEvse: ControlledModbusTcpPUTDto) =>
      updateEvse(installationId, evse.rawData?.deviceLinkId?.toString() ?? '', evse.rawData?.id?.toString() ?? '', newEvse, authFetch).then(() =>
        syncInstallation(installationId, authFetch)
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setChargeFallbackAllowed(currentChargeFallbackAllowed)
    },
  })

  const updateChargeFallbackAllowed = (newChargeFallbackAllowed: boolean): void => {
    if (chargeFallbackAllowed === newChargeFallbackAllowed) return

    const positiveFallbackMaxCurrent = evse.fallbackMaxCurrent || DEFAULT_EVSE_FALLBACK_MAX_CURRENT
    const fallbackMaxCurrent = newChargeFallbackAllowed ? positiveFallbackMaxCurrent : 0

    setChargeFallbackAllowed(newChargeFallbackAllowed)
    mutateEvse.mutate(getModbusTcpPUTDto({ ...evse, fallbackMaxCurrent }))
  }

  const isLoading = queryIsLoading || mutateEvse.isPending
  const isDisabled = isLoading || !userCanEdit
  const selectedConfig = OFFLINE_BEHAVIOUR_OPTIONS.find(opt => opt.value === chargeFallbackAllowed) ?? OFFLINE_BEHAVIOUR_OPTIONS[0]
  const defaultTabIndex = OFFLINE_BEHAVIOUR_OPTIONS.findIndex(opt => opt.value === currentChargeFallbackAllowed)

  return (
    <AccordionItem title={t('user-settings.evse-page.offline-behavior')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack>
        <SettingsBox
          title={t('user-settings.evse-page.offline-behavior')}
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: '50%' } }}>
              <TabButtonGroupBase
                sx={{ borderRadius: '100px' }}
                key={`lock-cable-${currentChargeFallbackAllowed}`}
                defaultTabIndex={defaultTabIndex > -1 ? defaultTabIndex : 0}
                disabled={isDisabled}
              >
                <TabButton
                  onClick={() => {
                    updateChargeFallbackAllowed(false)
                    triggerAppRating()
                  }}
                >
                  {t(OFFLINE_BEHAVIOUR_OPTIONS[0].labelKey)}
                </TabButton>
                <TabButton
                  onClick={() => {
                    updateChargeFallbackAllowed(true)
                    triggerAppRating()
                  }}
                >
                  {t(OFFLINE_BEHAVIOUR_OPTIONS[1].labelKey)}
                </TabButton>
              </TabButtonGroupBase>
            </Box>
          }
        />
        <SettingInfoBox title={t(selectedConfig.labelKey)} description={t(selectedConfig.descriptionKey)}>
          <Collapse sx={{ minWidth: '100%' }} in={chargeFallbackAllowed && !isLoading}>
            <OfflineBehaviourFallbackField
              installationId={installationId}
              queryIsLoading={isLoading}
              evse={evse}
              chargeFallbackAllowed={chargeFallbackAllowed}
            />
          </Collapse>
        </SettingInfoBox>
      </Stack>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </AccordionItem>
  )
}
